// Default design-token catalog: color schemes, spacing/radii/shadow
// scales. A ColorScheme is the per-section override version of the
// page-wide PalettePreset — same hierarchy of fields, applied via
// `--pg-*` CSS variables on the section wrapper so every block inside
// picks up the override.
//
// The default scheme library mirrors the seven page presets in
// `theme.ts` so that switching a section's color scheme produces a
// look consistent with switching the whole-page preset.

import { THEME_PRESETS, contrastRatio } from './theme'
import type {
  ColorScheme, DesignTokens, PalettePreset, RadiiTokens,
  ShadowTokens, SpacingTokens,
} from '@twasta/page-schema'

export const DEFAULT_SPACING: SpacingTokens = {
  xs: 4, sm: 8, md: 16, lg: 24, xl: 40, '2xl': 64,
}

export const DEFAULT_RADII: RadiiTokens = {
  none: 0, sm: 4, md: 8, lg: 16, full: 9999,
}

export const DEFAULT_SHADOWS: ShadowTokens = {
  none: 'none',
  sm: '0 1px 2px rgba(0,0,0,0.06)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 10px 24px rgba(0,0,0,0.10)',
  xl: '0 24px 48px rgba(0,0,0,0.16)',
}

// Build a ColorScheme from a PalettePreset. The required ColorScheme
// fields all have explicit values on the seven curated presets, so we
// can lift them straight; the optional hierarchy fields cascade through
// to the section's CSS vars too.
function paletteToScheme(id: string, name: string, p: PalettePreset): ColorScheme {
  // These are guaranteed-present on every preset's palette. Defaults
  // here are pure safety nets in case a future preset omits one.
  return {
    id, name,
    background: p.background || '#ffffff',
    surface: p.surface || '#f8fafc',
    text: p.text || '#1e293b',
    textSecondary: p.muted || p.textSecondary || '#475569',
    primary: p.primary || '#0ea5a4',
    onPrimary: p.onPrimary || '#ffffff',
    accent: p.accent || '#0284c7',
    border: p.border || '#e2e8f0',
    // Optional hierarchy — pass through whatever the preset declares.
    surfaceAlt: p.surfaceAlt, surfaceMuted: p.surfaceMuted, elevated: p.elevated,
    borderStrong: p.borderStrong,
    heading: p.heading, muted: p.muted, subtle: p.subtle,
    link: p.link, linkHover: p.linkHover,
    onSurface: p.onSurface, onSurfaceAlt: p.onSurfaceAlt,
    onAccent: p.onAccent, inverted: p.inverted,
    info: p.info, infoBg: p.infoBg,
    success: p.success, successBg: p.successBg,
    warning: p.warning, warningBg: p.warningBg,
    error: p.error, errorBg: p.errorBg,
  }
}

// Default library of section-level color schemes. One entry per page
// preset so the section dropdown reads as a "use this look just for
// this section" picker.
export const DEFAULT_COLOR_SCHEMES: ColorScheme[] = THEME_PRESETS.map(p =>
  paletteToScheme(`cs_${p.id}`, p.label, p.theme.palette || {}))

export function defaultDesignTokens(): DesignTokens {
  return {
    colorSchemes: DEFAULT_COLOR_SCHEMES,
    spacing: DEFAULT_SPACING,
    radii: DEFAULT_RADII,
    shadows: DEFAULT_SHADOWS,
  }
}

/**
 * Resolve a section's color-scheme override. Returns null when the section
 * doesn't opt into any scheme — the caller then falls back to the
 * page-wide PageTheme. There is intentionally no page-default fallback
 * here; page-wide colors live on PageTheme, not DesignTokens.
 */
export function resolveColorScheme(
  tokens: DesignTokens | undefined,
  sectionSchemeId: string | undefined,
): ColorScheme | null {
  if (!sectionSchemeId) return null
  const schemes = tokens?.colorSchemes ?? DEFAULT_COLOR_SCHEMES
  return schemes.find(s => s.id === sectionSchemeId) ?? null
}

/** CSS variable map for a color scheme — applied as `--pg-*` so blocks
 * inside an opted-in section pick up the override. Mirrors the variables
 * `themeCssVars()` writes at the page level so blocks that read e.g.
 * `--pg-info-bg` keep working under a section override too. */
export function colorSchemeToCssVars(s: ColorScheme): Record<string, string> {
  const out: Record<string, string> = {
    '--pg-bg': s.background,
    '--pg-surface': s.surface,
    '--pg-text': s.text,
    '--pg-text-secondary': s.textSecondary,
    '--pg-muted': s.muted ?? s.textSecondary,
    '--pg-primary': s.primary,
    '--pg-on-primary': s.onPrimary,
    '--pg-accent': s.accent,
    '--pg-border': s.border,
  }
  // Optional fields — only emit when present so we don't override the
  // page-level cascade with `undefined` strings.
  //
  // The four TEXT-ON-THIS-SECTION variables are the exception: they are
  // DERIVED from `text` when the scheme leaves them out, because leaving
  // them to the page-level cascade is never what an author means. A dark
  // band declaring `text: '#FFFFFF'` would otherwise keep the light page's
  // near-black `--pg-heading`, and every heading inside it — which is most
  // of what a band contains — rendered dark on dark. (`ColorScheme` already
  // documents its optional fields as auto-deriving; they were not.)
  const opt: Array<[string, string | undefined]> = [
    ['--pg-heading', s.heading ?? s.text],
    ['--pg-on-surface', s.onSurface ?? s.text],
    ['--pg-on-surface-alt', s.onSurfaceAlt ?? s.text],
    ['--pg-subtle', s.subtle ?? s.muted ?? s.textSecondary],
    ['--pg-link', s.link ?? schemeLink(s)],
    ['--pg-link-hover', s.linkHover ?? (s.link ? undefined : s.text)],
    ['--pg-on-accent', s.onAccent],
    ['--pg-inverted', s.inverted],
    ['--pg-surface-alt', s.surfaceAlt],
    ['--pg-surface-muted', s.surfaceMuted],
    ['--pg-elevated', s.elevated],
    ['--pg-border-strong', s.borderStrong],
    ['--pg-info', s.info], ['--pg-info-bg', s.infoBg],
    ['--pg-success', s.success], ['--pg-success-bg', s.successBg],
    ['--pg-warning', s.warning], ['--pg-warning-bg', s.warningBg],
    ['--pg-error', s.error], ['--pg-error-bg', s.errorBg],
  ]
  for (const [k, v] of opt) if (v) out[k] = v
  return out
}

/** A link colour for a scheme that declares none — derived, like the four
 * text variables above, because the page-level `--pg-link` it would
 * otherwise inherit was contrast-checked against the PAGE background. On a
 * dark band inside a light page that inherited link is the light page's
 * dark link: dark on dark, every footer/CTA link unreadable. First of
 * accent → primary → text that reads on this scheme's background. */
function schemeLink(s: ColorScheme): string {
  for (const c of [s.accent, s.primary]) {
    if (c && contrastRatio(c, s.background) >= 4.5) return c
  }
  return s.text
}

export function spacingToCssVars(s: SpacingTokens): Record<string, string> {
  return {
    '--pg-space-xs': `${s.xs}px`,
    '--pg-space-sm': `${s.sm}px`,
    '--pg-space-md': `${s.md}px`,
    '--pg-space-lg': `${s.lg}px`,
    '--pg-space-xl': `${s.xl}px`,
    '--pg-space-2xl': `${s['2xl']}px`,
  }
}
